import React, { useState } from 'react';
import { Phone, Mail, MessageSquare, ChevronRight, Share2, Headphones } from 'lucide-react';
import { COMPANY_INFO } from '../data/simatechData';

interface SideQuickDockProps {
  onOpenConnect: () => void;
  onOpenInquiry: (subject?: string) => void;
}

export const SideQuickDock: React.FC<SideQuickDockProps> = ({
  onOpenConnect,
  onOpenInquiry,
}) => {
  // Collapsed by default so only the arrow tab is visible until clicked
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  return (
    <div
      className="hidden sm:block fixed right-0 top-1/2 -translate-y-1/2 z-40 select-none"
      aria-label="Support & Quick Connect"
    >
      {/* 1. Collapsed State: Sleek Right-Edge Arrow Toggle Tab */}
      {!isExpanded ? (
        <div className="relative group">
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            aria-label="Open Support & Quick Connect Drawer"
            className="flex flex-col items-center gap-2 px-2.5 py-4 bg-[#061e24]/95 hover:bg-[#0b333c] text-white backdrop-blur-md rounded-none border-y border-l border-[#38c9de]/40 shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-300 hover:px-3 cursor-pointer group"
          >
            {/* Live Indicator Dot */}
            <span className="w-2 h-2 rounded-full bg-[#4ade80] shadow-[0_0_8px_#4ade80] animate-pulse" />

            {/* Sleek Minimal Headphones Icon */}
            <Headphones className="w-4 h-4 text-[#38c9de] group-hover:scale-110 transition-transform stroke-[1.75]" />

            {/* Vertical Label */}
            <span className="text-[11px] font-mono tracking-[0.25em] uppercase text-white/90 [writing-mode:vertical-rl] rotate-180 font-medium pt-1">
              SUPPORT
            </span>
          </button>

          {/* Hover Tooltip when collapsed */}
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap bg-[#0b333c] text-white text-xs font-medium px-3 py-1.5 rounded-none border border-[#1b7481]/40 shadow-xl">
            Click to open Support & Connect Dock
          </div>
        </div>
      ) : (
        /* 2. Expanded State: Full Quick Support Dock with Close/Collapse Arrow */
        <aside
          className="mr-3 sm:mr-5 flex flex-col items-center gap-2.5 p-2 bg-[#061e24]/95 backdrop-blur-xl rounded-none border border-[#1b7481]/50 shadow-[0_16px_40px_rgba(0,0,0,0.5)] animate-in fade-in slide-in-from-right-4 duration-200"
        >
          {/* Top Collapse Button (ChevronRight / Close Arrow) */}
          <div className="relative group">
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              aria-label="Collapse Support Dock"
              className="w-8 h-8 rounded-none bg-white/10 hover:bg-white/20 text-white/70 hover:text-white flex items-center justify-center transition-all duration-200 hover:scale-105 cursor-pointer mb-0.5"
            >
              <ChevronRight className="w-4 h-4 stroke-[2.5]" />
            </button>

            <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap bg-[#0b333c] text-white text-[11px] font-medium px-2.5 py-1 rounded-none border border-[#1b7481]/40 shadow-lg">
              Collapse
            </div>
          </div>

          {/* 1. Phone Circular Button */}
          <div className="relative group">
            <a
              href="tel:+919820234033"
              onMouseEnter={() => setHoveredItem('phone')}
              onMouseLeave={() => setHoveredItem(null)}
              aria-label="Call Simatech Automation"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-none bg-[#0b333c] hover:bg-[#1b7481] text-white/90 hover:text-white flex items-center justify-center transition-all duration-200 border border-white/10 hover:border-[#30b6cb]/50 shadow-sm hover:scale-105 cursor-pointer"
            >
              <Phone className="w-4 h-4 sm:w-[18px] sm:h-[18px] stroke-[1.8]" />
            </a>
            
            {/* Tooltip */}
            <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap bg-[#0b333c] text-white text-[11px] font-medium px-2.5 py-1 rounded-none border border-[#1b7481]/40 shadow-lg">
              Call: {COMPANY_INFO.phones[0]}
            </div>
          </div>

          {/* 2. Email Circular Button */}
          <div className="relative group">
            <a
              href={`mailto:${COMPANY_INFO.emails[0]}`}
              onMouseEnter={() => setHoveredItem('email')}
              onMouseLeave={() => setHoveredItem(null)}
              aria-label="Email Simatech Automation"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-none bg-[#0b333c] hover:bg-[#1b7481] text-white/90 hover:text-white flex items-center justify-center transition-all duration-200 border border-white/10 hover:border-[#30b6cb]/50 shadow-sm hover:scale-105 cursor-pointer"
            >
              <Mail className="w-4 h-4 sm:w-[18px] sm:h-[18px] stroke-[1.8]" />
            </a>

            {/* Tooltip */}
            <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap bg-[#0b333c] text-white text-[11px] font-medium px-2.5 py-1 rounded-none border border-[#1b7481]/40 shadow-lg">
              Email: {COMPANY_INFO.emails[0]}
            </div>
          </div>

          {/* 3. WhatsApp Circular Button with active dot */}
          <div className="relative group">
            <a
              href="https://wa.me/919820234033?text=Hello%20Simatech%20Automation%20Team%2C%20I%20would%20like%20to%20inquire%20about%20automation%20services."
              target="_blank"
              rel="noopener noreferrer"
              onMouseEnter={() => setHoveredItem('whatsapp')}
              onMouseLeave={() => setHoveredItem(null)}
              aria-label="Chat on WhatsApp"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-none bg-[#00a884] hover:bg-[#008f6f] text-white flex items-center justify-center transition-all duration-200 border border-white/20 shadow-[0_4px_14px_rgba(0,168,132,0.35)] hover:scale-105 cursor-pointer relative"
            >
              <MessageSquare className="w-4 h-4 sm:w-[18px] sm:h-[18px] stroke-[2]" />
              {/* Active Status Indicator Dot */}
              <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-[#4ade80] border-2 border-[#061e24] rounded-full animate-pulse" />
            </a>

            {/* Tooltip */}
            <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap bg-[#0b333c] text-white text-[11px] font-medium px-2.5 py-1 rounded-none border border-[#1b7481]/40 shadow-lg">
              WhatsApp Desk (Live)
            </div>
          </div>

          {/* 4. Full Connect Drawer Modal Opener */}
          <div className="relative group">
            <button
              type="button"
              onClick={onOpenConnect}
              onMouseEnter={() => setHoveredItem('drawer')}
              onMouseLeave={() => setHoveredItem(null)}
              aria-label="Open Full Connect Drawer"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-none bg-[#1b7481]/30 hover:bg-[#1b7481] text-[#30b6cb] hover:text-white flex items-center justify-center transition-all duration-200 border border-[#30b6cb]/40 hover:border-[#30b6cb] shadow-sm hover:scale-105 cursor-pointer"
            >
              <Share2 className="w-4 h-4 sm:w-[18px] sm:h-[18px] stroke-[2]" />
            </button>

            {/* Tooltip */}
            <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap bg-[#0b333c] text-white text-[11px] font-medium px-2.5 py-1 rounded-none border border-[#1b7481]/40 shadow-lg">
              Socials & Full Contact Hub
            </div>
          </div>
        </aside>
      )}
    </div>
  );
};
